// 2026-05-26 - FormuLearning - Step 2 카드 컨테이너
// 2026-09-23 수정 - 이슈 #83: 물성 예측을 진행 중 작업 잠금(#82 `busyStore`)에 등록하고 **Break** 를 둔다 — abort 후
//                   저장본 재조회(서버는 끝에서 한 번 저장하므로 중단된 예측은 남지 않는다). abort 는 일괄 경로로 폴백하지 않는다.
// 2026-10-29 수정 - 이슈 #111: PhProfileSection 에 pH 프로파일 신뢰도(`_profile_quality`, M1016-11) 전달.
// 2026-11-03 수정 - 이슈 #112: LabDataSection 에 projectId·onPersist(사진 업로드 직전 lab_data 즉시 저장).
// 2026-11-03 수정 - 이슈 #104: 물성 표 아래 CATMoS 급성 경구 독성 소절(`ToxicitySection`).
// 파일 위치: frontend/src/features/step2/Step2Card.tsx

import { useEffect, useState, useCallback, useRef } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { getStep2, predictProperties, predictPropertiesStream, updateStep2 } from '../../api/step2'
import type { Step2Data } from '../../api/step2'
import { getStep1 } from '../../api/step1'
import { useDeferredSave } from '../../hooks/useDeferredSave'
import { useSummaryStore } from '../../stores/summaryStore'
import { useMoleculeStore } from '../../stores/moleculeStore'
import { useBusyStore } from '../../stores/busyStore'
import { toast } from '../../stores/toastStore'
import PhyschemSection from './PhyschemSection'
import ToxicitySection from './ToxicitySection'
import PhProfileSection from './PhProfileSection'
import LabDataSection from './LabDataSection'
import type { LabDataValues } from './LabDataSection'

export default function Step2Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  const [data, setData] = useState<Step2Data | null>(null)
  const [predicting, setPredicting] = useState(false)
  // 2026-09-23 - 이슈 #83: 진행 중 예측 1건(중단·언마운트 판정용 ref) · 도착한 결과 · Break 왕복 중.
  const runRef = useRef<{ id: string; ctrl: AbortController; stopped: boolean } | null>(null)
  const [arrived, setArrived] = useState<string[]>([])
  const [breaking, setBreaking] = useState(false)
  // 2026-08-04 - 이슈 #56: SMILES 를 로컬 state 가 아닌 게시 스토어에서 구독한다. 종전에는 마운트
  // 시점에 읽은 값을 그대로 들고 있어, 같은 화면에서 Step1 검색을 마쳐도 반영되지 않았다.
  const smiles = useMoleculeStore((s) => s.smiles)
  const setSmiles = useMoleculeStore((s) => s.setSmiles)

  // 2026-07-09 - 이슈 #33 4단계: 필드 편집을 Autosave 모드에 맞춰 저장(ON=즉시, OFF=버퍼).
  const { apply } = useDeferredSave<Step2Data>({ step: 2, projectId, data, setData, save: updateStep2 })
  const setSummary = useSummaryStore((s) => s.setSummary)

  // 프로젝트 전환·언마운트 — 진행 중 예측을 확인 없이 끊는다(Step 1 `BR-S1-10` 과 같다). 서버는 저장하지 않는다.
  useEffect(() => () => {
    const run = runRef.current
    if (run) {
      run.stopped = true
      run.ctrl.abort()
    }
  }, [projectId])

  useEffect(() => {
    if (!projectId) return
    getStep2(projectId).then(setData)
    // Step1 카드가 접혀 있으면 발행자(Step1Card)가 언마운트 상태이므로, 스토어를 서버 값으로 초기 충전한다.
    getStep1(projectId).then((s1) => setSmiles(s1.canonical_smiles))
  }, [projectId, setSmiles])

  // 2026-07-18 - 이슈 #40: 영속 AI Summary(Vertex, predict 시 생성)를 발행. 표시는 CardShell 위젯이 전담.
  useEffect(() => {
    if (!data) return
    setSummary(2, data.ai_summary, data.ai_summary_ko)
  }, [data, setSummary])

  // 2026-07-29 - 이슈 #51: DB Search·AI Prediction 이 백엔드에서 병렬로 돌고 먼저 끝난
  // 쪽부터 도착한다. 종전에는 셋을 순차 실행해 전체(약 15초)를 통째로 기다렸다.
  const handlePredict = useCallback(async () => {
    if (!projectId || !smiles || runRef.current) return
    const run = { id: crypto.randomUUID(), ctrl: new AbortController(), stopped: false }
    runRef.current = run
    setArrived([])
    setPredicting(true)
    try {
      await predictPropertiesStream(projectId, smiles, (ev) => {
        if (run.stopped) return
        if (ev.event === 'db_search' || ev.event === 'ai_prediction' || ev.event === 'summary') {
          setArrived((a) => [...a, ev.event])
        }
        if (ev.event === 'db_search') {
          setData((d) => (d ? { ...d, db_search: ev.data as Step2Data['db_search'] } : d))
        } else if (ev.event === 'ai_prediction') {
          setData((d) => (d ? { ...d, ai_prediction: ev.data as Step2Data['ai_prediction'] } : d))
        } else if (ev.event === 'summary') {
          setData((d) => (d ? {
            ...d,
            ai_summary: (ev.data.ai_summary as string) ?? null,
            ai_summary_ko: (ev.data.ai_summary_ko as string) ?? null,
          } : d))
        }
      }, run.ctrl.signal)
    } catch {
      if (run.stopped) return     // Break·언마운트 — 폴백하면 중단한 예측이 다시 돈다
      // 스트림 실패 시 일괄 경로로 폴백 — 값을 못 받는 것보다 늦게라도 받는 편이 낫다.
      await predictProperties(projectId, smiles)
      if (runRef.current === run) setData(await getStep2(projectId))
    } finally {
      if (runRef.current === run) {
        runRef.current = null
        setPredicting(false)
        setBreaking(false)
      }
    }
  }, [projectId, smiles])

  // 진행 요약 — 확인창·헤더 표시가 같이 쓴다. 두 조회가 병렬이라 «진행 중» 은 아직 안 온 쪽 이름이다.
  const progress = (() => {
    const waiting = [['db_search', 'DB Search'], ['ai_prediction', 'AI Prediction']]
      .filter(([k]) => !arrived.includes(k)).map(([, label]) => label)
    return { done: arrived.length, total: 3, doing: waiting.length ? waiting.join('·') : 'AI Summary' }
  })()

  // 2026-09-23 - 이슈 #83: 중단 — 확인 → abort → 저장본 재조회. 서버는 Summary 뒤에 한 번 저장하므로 끊긴 예측은 남지
  // 않는다. 저장 직후에 끊겼다면 재조회가 새 결과를 보인다 — 어느 쪽이든 화면이 저장본과 같아진다.
  const handleBreak = useCallback(async () => {
    const run = runRef.current
    if (!projectId || !run || run.stopped) return
    const ok = window.confirm(
      `물성 예측을 중단할까요?\n\n${progress.total}개 중 ${progress.done}개 완료, ${progress.doing} 진행 중입니다.\n`
        + '중단하면 이번 예측 결과는 저장되지 않고 이전 결과로 돌아갑니다.',
    )
    if (!ok || runRef.current !== run) return
    run.stopped = true
    setBreaking(true)
    run.ctrl.abort()
    try {
      setData(await getStep2(projectId))
      toast('물성 예측을 중단했습니다. 저장된 결과를 다시 불러왔습니다.', 'info')
    } catch {
      toast('물성 예측을 중단했으나 저장된 결과를 불러오지 못했습니다. 새로고침하세요.', 'warn')
    }
  }, [projectId, progress.total, progress.done, progress.doing])

  // 진행 중 작업 전역 등록(#82·#83) — `predicting` 에 묶여 종료·언마운트 모두에서 해제된다.
  const handleBreakRef = useRef(handleBreak)
  useEffect(() => { handleBreakRef.current = handleBreak })
  const busyIdRef = useRef<string | null>(null)
  useEffect(() => {
    const id = predicting ? runRef.current?.id : undefined
    if (!id) return
    busyIdRef.current = id
    useBusyStore.getState().begin({ id, step: 2, label: 'Step 2 물성 예측', cancel: () => { void handleBreakRef.current() } })
    return () => {
      busyIdRef.current = null
      useBusyStore.getState().end(id)
    }
  }, [predicting])
  useEffect(() => {
    const id = busyIdRef.current
    if (!id) return
    useBusyStore.getState().update(id, {
      phase: breaking ? 'breaking' : 'running',
      progress: { done: progress.done, total: progress.total, doing: progress.doing },
    })
  }, [predicting, breaking, progress.done, progress.total, progress.doing])

  const handleSelectSource = useCallback(async (prop: string, source: 'db' | 'ai') => {
    if (!data) return
    await apply({ selected_source: { ...data.selected_source, [prop]: source } })
  }, [data, apply])

  const handleSaveLabData = useCallback(async (labData: LabDataValues) => {
    await apply({ lab_data: labData as unknown as Record<string, unknown> })
  }, [apply])
  // 2026-11-03 - 이슈 #112: 사진 슬롯 키(행 uid)를 서버에 고정 — Autosave OFF 버퍼와 무관하게 lab_data 만 즉시 PUT.
  const handlePersistLabData = useCallback(async (labData: LabDataValues) => {
    if (!projectId) return
    await updateStep2(projectId, { lab_data: labData as unknown as Record<string, unknown> })
  }, [projectId])

  if (!data) return <p className="text-xs text-text-secondary">Loading...</p>

  return (
    <div>
      <PhyschemSection
        dbSearch={data.db_search}
        aiPrediction={data.ai_prediction}
        selectedSource={data.selected_source}
        onSelectSource={handleSelectSource}
        onPredict={handlePredict}
        predicting={predicting}
        onBreak={handleBreak}
        breaking={breaking}
        smiles={smiles}
      />
      {/* 2026-11-03 - 이슈 #104: CATMoS 급성 경구 독성 — 물성 표 바로 아래 별도 소절. */}
      <ToxicitySection toxicity={data.ai_prediction._toxicity} />
      {/* 2026-08-24 - M84-45(이슈 #62): 표에서 뺀 pH별 LogD 를 곡선으로 되돌리고 용해도를 함께 그린다. */}
      <PhProfileSection profile={data.ai_prediction._ph_profile} quality={data.ai_prediction._profile_quality} />
      <LabDataSection projectId={projectId} labData={data.lab_data} onSave={handleSaveLabData} onPersist={handlePersistLabData} />
    </div>
  )
}
